/**
 * The Live now block's countdown (ADR 0033, second amendment of 2026-10-03): "Starts in 4:32" before a service, and
 * the one piece of script a church page may carry. The server prints the countdown's text correct for the instant it
 * answers; this script only keeps the text moving and, at the start, reloads the page once so the player appears
 * without anyone pressing anything. The switch itself is the server's: the reloaded page is drawn live because the
 * server's clock says so, not because the script did anything to the page.
 *
 * This file imports nothing. `runLiveCountdowns` and `formatCountdown` are written into the page as text
 * (`LIVE_COUNTDOWN_SCRIPT`), so each must be self-contained: no imports, no module state, nothing from outside but its
 * arguments and the browser globals named in `LIVE_COUNTDOWN_SCRIPT` itself.
 */

/**
 * The countdown's words for what is left, rounded up to the next second so it never says 0:00 before the instant:
 * "Starts in 4:32" under an hour, "Starts in 2:04:32" under a day, "Starts in 2 days 3 hours" beyond. `label` is
 * "Starts in" when the player opens at the start, and "Opens in" when the block opens it early (ADR 0033, third
 * amendment of 2026-10-03): the countdown then counts to the opening, and says so.
 */
export function formatCountdown(remainingMs: number, label = "Starts in"): string {
  const total = Math.max(0, Math.ceil(remainingMs / 1000));
  const two = (n: number) => (n < 10 ? `0${String(n)}` : String(n));
  const days = Math.floor(total / 86_400);
  const hours = Math.floor((total % 86_400) / 3_600);
  const minutes = Math.floor((total % 3_600) / 60);
  const seconds = total % 60;
  if (days > 0) {
    const d = `${String(days)} ${days === 1 ? "day" : "days"}`;
    return hours === 0
      ? `${label} ${d}`
      : `${label} ${d} ${String(hours)} ${hours === 1 ? "hour" : "hours"}`;
  }
  if (hours > 0) return `${label} ${String(hours)}:${two(minutes)}:${two(seconds)}`;
  return `${label} ${String(minutes)}:${two(seconds)}`;
}

/** The parts of a countdown element the script touches. */
export interface CountdownElement {
  textContent: string | null;
  getAttribute(name: string): string | null;
  readonly parentElement: {
    querySelector(selector: string): { textContent: string | null } | null;
  } | null;
}

/** Everything the script reads from the browser, so a test can run it with fakes. */
export interface CountdownEnvironment {
  readonly document: { querySelectorAll(selector: string): ArrayLike<CountdownElement> };
  now(): number;
  setInterval(tick: () => void, ms: number): unknown;
  clearInterval(id: unknown): void;
  setTimeout(run: () => void, ms: number): unknown;
  reload(): void;
  /** `sessionStorage`, or null where the browser refuses it (a private window may). */
  readonly storage: {
    getItem(key: string): string | null;
    setItem(key: string, value: string): void;
  } | null;
  random(): number;
}

/** How many times one start may reload the page, so a visitor whose clock runs fast is not reloaded forever. */
export const COUNTDOWN_MAX_RELOADS = 3;

/**
 * Keeps every countdown on the page moving, once a second, and at the opening (the start, or the start less the block's
 * open-early minutes: `data-opens-at`) announces "Live now" politely and reloads
 * the page after one to two seconds (spread, so a congregation's browsers do not all ask in the same millisecond).
 *
 * The clock is the visitor's until the page has been reloaded for this start once; after that the server's instant
 * the page was drawn at (`data-rendered-at`) corrects it, so a visitor whose clock is fast is not reloaded again
 * before the server is live. Without session storage there is no record of a reload, so the script never reloads:
 * it says "Live now: refresh the page to watch" instead.
 */
export function runLiveCountdowns(
  env: CountdownEnvironment,
  format: (remainingMs: number, label?: string) => string,
): void {
  const elements = Array.from(env.document.querySelectorAll("[data-live-countdown]"));
  if (elements.length === 0) return;
  const read = (key: string): number => {
    try {
      return Number(env.storage?.getItem(key) ?? "0") || 0;
    } catch {
      return 0;
    }
  };
  const bootNow = env.now();
  const first = elements[0];
  const firstStart = Number(first?.getAttribute("data-opens-at"));
  const renderedAt = Number(first?.getAttribute("data-rendered-at"));
  const key = `how-live-reload:${String(firstStart)}`;
  const attempts = read(key);
  // A page reloaded for this start was drawn just now by the server: trust its clock over the visitor's.
  const offset = attempts > 0 && Number.isFinite(renderedAt) ? renderedAt - bootNow : 0;
  let done = false;
  let id: unknown = null;
  const tick = (): void => {
    if (done) return;
    const now = env.now() + offset;
    let started = false;
    for (const element of elements) {
      const opensAt = Number(element.getAttribute("data-opens-at"));
      if (!Number.isFinite(opensAt)) continue;
      const left = opensAt - now;
      if (left > 0) {
        const text = format(left, element.getAttribute("data-countdown-label") ?? undefined);
        if (element.textContent !== text) element.textContent = text;
      } else {
        started = true;
      }
    }
    if (!started) return;
    done = true;
    if (id !== null) env.clearInterval(id);
    // 3 is COUNTDOWN_MAX_RELOADS, written out: this function is copied into the page and cannot see the module.
    let canReload = env.storage !== null && attempts < 3;
    if (canReload) {
      try {
        env.storage?.setItem(key, String(attempts + 1));
      } catch {
        canReload = false;
      }
    }
    for (const element of elements) {
      const opening = element.getAttribute("data-countdown-label") === "Opens in";
      element.textContent = canReload
        ? opening
          ? "Opening now"
          : "Starting now"
        : "Live now: refresh the page to watch";
      const announce = element.parentElement?.querySelector("[data-live-announce]");
      if (announce) announce.textContent = "Live now";
    }
    if (canReload) {
      env.setTimeout(
        () => {
          env.reload();
        },
        1000 + Math.floor(env.random() * 1000),
      );
    }
  };
  id = env.setInterval(tick, 1000);
  tick();
}

/**
 * The script as the page carries it: the two functions above, written out and called with the browser's own clock,
 * timers and session storage. Inline, so a page that shows a countdown makes no extra request and a page that does
 * not carries nothing. Under a content security policy that forbids inline script it would simply not run, and the
 * printed text and the server's switch still hold.
 */
export const LIVE_COUNTDOWN_SCRIPT = `(${runLiveCountdowns.toString()})({document:document,now:function(){return Date.now()},setInterval:function(f,m){return setInterval(f,m)},clearInterval:function(i){clearInterval(i)},setTimeout:function(f,m){return setTimeout(f,m)},reload:function(){location.reload()},storage:(function(){try{return window.sessionStorage}catch(e){return null}})(),random:function(){return Math.random()}},${formatCountdown.toString()});`;
